/**
 * v3.18-1295 — Contact editor dialog (create / edit).
 *
 * Supports unlimited phone numbers (mobile / office / home / extension /
 * WhatsApp / fax), unlimited emails, group assignment, favorite +
 * primary flags, timezone, notes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Phone, Mail, Star, Users, Building2, Clock } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  createContact, updateContact, PHONE_LABELS, EMAIL_LABELS,
} from '../lib/communication_contacts';

const blank = (companyId, clientId) => ({
  company_id: companyId || '',
  client_id: clientId || null,
  full_name: '',
  title: '',
  department: '',
  timezone: '',
  notes: '',
  is_primary: false,
  is_favorite: false,
  group_ids: [],
  phones: [{ label: 'mobile', number: '', extension: '', is_primary: true }],
  emails: [{ label: 'work', address: '', is_primary: true }],
});

const withIds = (list, keyField) => (list || []).map((row, i) => ({
  ...row,
  [keyField]: row[keyField] ?? `_new-${i}-${Math.random().toString(36).slice(2, 8)}`,
}));

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix',
  'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu',
  'Europe/London', 'Europe/Paris', 'Asia/Tokyo', 'Australia/Sydney',
];

const ContactEditorDialog = ({
  open, onOpenChange, contact, companyId, clientId, groups = [], onSaved,
}) => {
  const [form, setForm] = useState(blank(companyId, clientId));
  const [saving, setSaving] = useState(false);
  const isEdit = !!contact?.id;

  useEffect(() => {
    if (!open) return;
    if (contact) {
      setForm({
        ...contact,
        phones: withIds(contact.phones?.length ? contact.phones : blank().phones, 'id'),
        emails: withIds(contact.emails?.length ? contact.emails : blank().emails, 'id'),
        group_ids: contact.group_ids || [],
      });
    } else {
      setForm(blank(companyId, clientId));
    }
  }, [open, contact, companyId, clientId]);

  const addPhone = () => setForm((f) => ({
    ...f,
    phones: [...f.phones, { label: 'mobile', number: '', extension: '', is_primary: false }],
  }));
  const removePhone = (idx) => setForm((f) => ({
    ...f, phones: f.phones.filter((_, i) => i !== idx),
  }));
  const updatePhone = (idx, patch) => setForm((f) => ({
    ...f,
    phones: f.phones.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
  }));
  const setPhonePrimary = (idx) => setForm((f) => ({
    ...f,
    phones: f.phones.map((p, i) => ({ ...p, is_primary: i === idx })),
  }));

  const addEmail = () => setForm((f) => ({
    ...f, emails: [...f.emails, { label: 'work', address: '', is_primary: false }],
  }));
  const removeEmail = (idx) => setForm((f) => ({
    ...f, emails: f.emails.filter((_, i) => i !== idx),
  }));
  const updateEmail = (idx, patch) => setForm((f) => ({
    ...f,
    emails: f.emails.map((e, i) => (i === idx ? { ...e, ...patch } : e)),
  }));
  const setEmailPrimary = (idx) => setForm((f) => ({
    ...f,
    emails: f.emails.map((e, i) => ({ ...e, is_primary: i === idx })),
  }));

  const toggleGroup = (gid) => setForm((f) => {
    const set = new Set(f.group_ids || []);
    if (set.has(gid)) set.delete(gid); else set.add(gid);
    return { ...f, group_ids: Array.from(set) };
  });

  const submit = async () => {
    if (!form.full_name?.trim()) {
      toast.error('Full name is required');
      return;
    }
    if (!form.company_id) {
      toast.error('Company is required');
      return;
    }
    const clean = {
      ...form,
      phones: (form.phones || []).filter((p) => (p.number || '').trim())
        .map(({ id, ...rest }) => (typeof id === 'string' && id.startsWith('_new-') ? rest : { id, ...rest })),
      emails: (form.emails || []).filter((e) => (e.address || '').trim())
        .map(({ id, ...rest }) => (typeof id === 'string' && id.startsWith('_new-') ? rest : { id, ...rest })),
    };
    setSaving(true);
    try {
      const result = isEdit
        ? await updateContact(contact.id, clean)
        : await createContact(clean);
      toast.success(isEdit ? 'Contact updated' : 'Contact created');
      onSaved?.(result);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="contact-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
          <DialogDescription>
            Every contact in the directory can be called, texted, and emailed
            directly. Fill in as much detail as you need.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Identity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Full name *</Label>
              <Input
                value={form.full_name || ''}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                data-testid="contact-editor-name"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs">Title</Label>
              <Input
                value={form.title || ''}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="VP Sales"
                data-testid="contact-editor-title"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1">
                <Building2 className="h-3 w-3" /> Department
              </Label>
              <Input
                value={form.department || ''}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                data-testid="contact-editor-department"
              />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1">
                <Clock className="h-3 w-3" /> Time zone (for quiet hours)
              </Label>
              <Select
                value={form.timezone || 'none'}
                onValueChange={(v) => setForm({ ...form, timezone: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="contact-editor-timezone">
                  <SelectValue placeholder="Auto-detect" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Auto-detect</SelectItem>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Flags */}
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={!!form.is_primary}
                onCheckedChange={(v) => setForm({ ...form, is_primary: !!v })}
                data-testid="contact-editor-is-primary"
              />
              <span className="text-xs">Primary contact for this client</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={!!form.is_favorite}
                onCheckedChange={(v) => setForm({ ...form, is_favorite: !!v })}
                data-testid="contact-editor-is-favorite"
              />
              <span className="text-xs flex items-center gap-1">
                <Star className="h-3 w-3" /> Favorite
              </span>
            </label>
          </div>

          {/* Phones */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs flex items-center gap-1">
                <Phone className="h-3 w-3" /> Phone numbers
              </Label>
              <Button
                size="sm" variant="outline" onClick={addPhone}
                data-testid="contact-editor-add-phone"
                className="h-7 text-xs"
              >
                <Plus className="h-3 w-3 mr-1" /> Add phone
              </Button>
            </div>
            <div className="space-y-2">
              {(form.phones || []).map((p, i) => (
                <div key={p.id || i} className="border border-slate-100 rounded p-2 space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Select
                      value={p.label || 'mobile'}
                      onValueChange={(v) => updatePhone(i, { label: v })}
                    >
                      <SelectTrigger className="w-28" data-testid={`contact-editor-phone-${i}-label`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PHONE_LABELS.map((l) => (
                          <SelectItem key={l} value={l}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={p.number || ''}
                      onChange={(e) => updatePhone(i, { number: e.target.value })}
                      placeholder="555-100-2000"
                      className="flex-1"
                      data-testid={`contact-editor-phone-${i}-number`}
                    />
                    {p.label === 'extension' && (
                      <Input
                        value={p.extension || ''}
                        onChange={(e) => updatePhone(i, { extension: e.target.value })}
                        placeholder="ext"
                        className="w-24"
                        data-testid={`contact-editor-phone-${i}-ext`}
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => setPhonePrimary(i)}
                      title={p.is_primary ? 'Primary phone' : 'Mark primary'}
                      data-testid={`contact-editor-phone-${i}-primary`}
                      className={`h-8 w-8 rounded flex items-center justify-center transition
                        ${p.is_primary ? 'bg-amber-100 text-amber-600' : 'text-slate-300 hover:text-amber-500'}`}
                    >
                      <Star className="h-3.5 w-3.5" fill={p.is_primary ? 'currentColor' : 'none'} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removePhone(i)}
                      className="h-8 w-8 text-slate-400 hover:text-red-500"
                      data-testid={`contact-editor-phone-${i}-remove`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 pl-1 text-[11px] text-slate-500">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <Checkbox
                        checked={!!p.is_preferred}
                        onCheckedChange={(v) => updatePhone(i, { is_preferred: !!v })}
                        data-testid={`contact-editor-phone-${i}-preferred`}
                      />
                      Preferred
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <Checkbox
                        checked={!!p.is_verified}
                        onCheckedChange={(v) => updatePhone(i, { is_verified: !!v })}
                        data-testid={`contact-editor-phone-${i}-verified`}
                      />
                      Verified
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <Checkbox
                        checked={!!p.do_not_call}
                        onCheckedChange={(v) => updatePhone(i, { do_not_call: !!v })}
                        data-testid={`contact-editor-phone-${i}-dnc`}
                      />
                      <span className="text-red-600 font-medium">Do Not Call</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Emails */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="text-xs flex items-center gap-1">
                <Mail className="h-3 w-3" /> Email addresses
              </Label>
              <Button
                size="sm" variant="outline" onClick={addEmail}
                data-testid="contact-editor-add-email"
                className="h-7 text-xs"
              >
                <Plus className="h-3 w-3 mr-1" /> Add email
              </Button>
            </div>
            <div className="space-y-2">
              {(form.emails || []).map((e, i) => (
                <div key={e.id || i} className="flex items-center gap-1.5">
                  <Select
                    value={e.label || 'work'}
                    onValueChange={(v) => updateEmail(i, { label: v })}
                  >
                    <SelectTrigger className="w-28" data-testid={`contact-editor-email-${i}-label`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EMAIL_LABELS.map((l) => (
                        <SelectItem key={l} value={l}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={e.address || ''}
                    onChange={(ev) => updateEmail(i, { address: ev.target.value })}
                    placeholder="name@company.com"
                    className="flex-1"
                    data-testid={`contact-editor-email-${i}-address`}
                  />
                  <button
                    type="button"
                    onClick={() => setEmailPrimary(i)}
                    title={e.is_primary ? 'Primary email' : 'Mark primary'}
                    data-testid={`contact-editor-email-${i}-primary`}
                    className={`h-8 w-8 rounded flex items-center justify-center transition
                      ${e.is_primary ? 'bg-amber-100 text-amber-600' : 'text-slate-300 hover:text-amber-500'}`}
                  >
                    <Star className="h-3.5 w-3.5" fill={e.is_primary ? 'currentColor' : 'none'} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeEmail(i)}
                    className="h-8 w-8 text-slate-400 hover:text-red-500"
                    data-testid={`contact-editor-email-${i}-remove`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Groups */}
          {groups.length > 0 && (
            <div>
              <Label className="text-xs flex items-center gap-1 mb-1.5">
                <Users className="h-3 w-3" /> Groups
              </Label>
              <div className="flex flex-wrap gap-1.5">
                {groups.map((g) => {
                  const active = (form.group_ids || []).includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => toggleGroup(g.id)}
                      data-testid={`contact-editor-group-${g.id}`}
                      className={`px-2 py-0.5 text-xs rounded-full border transition
                        ${active ? 'border-transparent text-white' : 'border-slate-200 text-slate-600 hover:border-slate-400'}`}
                      style={active ? { backgroundColor: g.color || '#6366F1' } : undefined}
                    >
                      {g.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <Label className="text-xs">Notes</Label>
            <Textarea
              value={form.notes || ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              data-testid="contact-editor-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="contact-editor-save"
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create contact'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContactEditorDialog;
